/**
 * How Drumkit should read a SharePoint Excel workbook for load building.
 *
 * - **agile-ewa**: Syfan weekly sheets — extension EWA scrape only (no Microsoft Graph).
 * - **sharepoint-graph**: backend-produced Graph suggestions; EWA/DOM is used only for active worksheet selection.
 *
 * Classification uses the browser document URL (host / site path), not grid headers.
 */
import { isSharepointExcelWorkbookTabUrl } from 'constants/SharepointTrustedWorkbookUrls';

export type SharepointLoadBuildingDataSource = 'agile-ewa' | 'sharepoint-graph';

/** Syfan Agile workbooks (prod). */
const SYFAN_AGILE_URL_MARKERS = ['agilecoldstorage'] as const;

function normalizedUrlParts(rawUrl: string): {
  lower: string;
  host: string;
  path: string;
} {
  const trimmed = rawUrl.trim();
  try {
    const u = new URL(trimmed);
    return {
      lower: trimmed.toLowerCase(),
      host: u.hostname.toLowerCase(),
      path: `${u.pathname}${u.search}`.toLowerCase(),
    };
  } catch {
    const lower = trimmed.toLowerCase();
    return { lower, host: '', path: lower };
  }
}

function urlMatchesSyfanAgile(
  lower: string,
  host: string,
  path: string
): boolean {
  const haystack = `${host}${path}`;
  return (
    SYFAN_AGILE_URL_MARKERS.some((m) => haystack.includes(m)) ||
    lower.includes('agilecoldstorage')
  );
}

/**
 * Maps the browser document URL to a SharePoint load-building data source.
 *
 * Rules (in order):
 * 1. Syfan prod host/path (`agilecoldstorage`) → EWA only.
 * 2. Any other trusted SharePoint workbook host → Graph.
 * 3. Otherwise → `null` (no SharePoint load-building sync for this URL).
 */
export function sharepointLoadBuildingDataSourceFromUrl(
  rawUrl: string
): SharepointLoadBuildingDataSource | null {
  const { lower, host, path } = normalizedUrlParts(rawUrl);
  if (!lower.includes('sharepoint')) {
    return null;
  }

  if (urlMatchesSyfanAgile(lower, host, path)) {
    return 'agile-ewa';
  }
  if (isSharepointExcelWorkbookTabUrl(rawUrl)) {
    return 'sharepoint-graph';
  }

  return null;
}

/** Whether `POST /sharepoint/workbooks/load-suggestions` should run for this document. */
export function sharepointGraphApiAllowedForDocument(
  documentUrl: string
): boolean {
  return (
    sharepointLoadBuildingDataSourceFromUrl(documentUrl) === 'sharepoint-graph'
  );
}

/** Whether the extension should parse and cache Syfan Agile rows from EWA prefetch. */
export function sharepointEwaAgileCacheAllowedForDocument(
  documentUrl: string
): boolean {
  return sharepointLoadBuildingDataSourceFromUrl(documentUrl) === 'agile-ewa';
}
